import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Calendar, Clock, User, ExternalLink, BookOpen, Star, Share2, Eye, AlertCircle, Loader2, Mail } from 'lucide-react';
import Navbar from '../components/Navbar';
import { Footer2 } from '../components/Footer2';
import { supabase } from '../lib/supabase';

// Simple Button component since we're not using shadcn
const Button = ({ variant = "default", size = "default", children, className = "", onClick, ...props }) => {
  const baseClasses = "inline-flex items-center justify-center font-medium transition-all duration-200";
  
  const sizeClasses = {
    default: "px-4 py-2",
    sm: "px-3 py-1 text-sm"
  };
  
  const variantClasses = variant === "outline" 
    ? "border-2 border-black bg-white text-black hover:bg-gray-50 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]"
    : "bg-[#0395fc] text-white border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]";
  
  const combinedClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses} ${className}`;

  return (
    <button className={combinedClasses} onClick={onClick} {...props}>
      {children}
    </button>
  );
};

const ArticleOfTheWeek = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true, threshold: 0.1 });
  const [currentArticle, setCurrentArticle] = useState(null);
  const [previousArticles, setPreviousArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Fetch articles from database
  const fetchArticles = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: articles, error: fetchError } = await supabase
        .from('articles')
        .select(`
          id,
          title,
          content,
          slug,
          article_type,
          status,
          published_at,
          featured_image_url,
          tags,
          author_id,
          members!articles_author_id_fkey (
            first_name,
            last_name,
            display_name
          )
        `)
        .eq('article_type', 'article_of_week')
        .eq('status', 'published')
        .order('published_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      if (articles && articles.length > 0) {
        // Sort articles by published date (most recent first)
        const sortedArticles = articles.sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
        
        // First article is the current/most recent (Week 1)
        const current = sortedArticles[0];
        const formattedCurrent = {
          id: current.id,
          title: current.title,
          subtitle: '', // Not in database schema
          author: 'Anand Lo',
          authorInfo: '', // Could be added later
          publishDate: new Date(current.published_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          readTime: '15 min read', // Could be calculated from content length
          difficulty: 'Advanced', // Could be added to database
          category: 'Research Paper', // Could be derived from tags
          tags: current.tags || [],
          summary: current.content ? current.content.substring(0, 300) + '...' : 'No summary available.',
          keyTakeaways: [], // Could be added to database
          originalPaper: '#', // Could be added to database
          additionalResources: [], // Could be added to database
          stats: {
            views: '1.2K',
            likes: '84',
            downloads: '32',
            citations: '12'
          },
          weekNumber: 1, // Most recent article is always Week 1
          year: new Date(current.published_at).getFullYear(),
          featured_image_url: current.featured_image_url,
          slug: current.slug
        };

        // Rest are previous articles with week numbers 2, 3, 4, etc.
        const previous = sortedArticles.slice(1).map((article, index) => ({
          id: article.id,
          title: article.title,
          author: article.members?.display_name || `${article.members?.first_name || ''} ${article.members?.last_name || ''}`.trim() || 'DMLS Team',
          publishDate: new Date(article.published_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }),
          category: 'Research Paper',
          readTime: '12 min read',
          weekNumber: index + 2, // Week 2, 3, 4, etc.
          year: new Date(article.published_at).getFullYear(),
          slug: article.slug,
          featured_image_url: article.featured_image_url
        }));

        setCurrentArticle(formattedCurrent);
        setPreviousArticles(previous);
      }
    } catch (err) {
      console.error('Error fetching articles:', err.message || err);
      setError('Failed to load articles. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showShareMenu && !event.target.closest('.share-menu-container')) {
        setShowShareMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showShareMenu]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      alert('Could not copy link. Please try again.');
    }
  };

  const handleShareToSocial = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(currentArticle?.title || 'Article of the Week');
    const description = encodeURIComponent(currentArticle?.summary || '');
    
    let shareUrl = '';
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${title}&url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${title}&body=${description}%0A%0A${url}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowShareMenu(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section ref={heroRef} className="pt-24 pb-16 bg-cover bg-center bg-no-repeat relative overflow-hidden" style={{ backgroundImage: 'url(/15.jpg)' }}>
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/50 z-0"></div>
        
        {/* Animated background elements */}
        <motion.div 
          className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full blur-2xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-20 left-20 w-24 h-24 bg-yellow-300/20 rounded-full blur-xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
           
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl lg:text-6xl font-black mb-6 leading-tight"
            >
              Article of the Week
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed"
            >
              Explore clear, concise breakdowns of emerging trends and developments in AI and machine learning.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Current Article Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-[#0395fc]" />
                <span className="text-lg font-medium text-muted-foreground">Loading articles...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mx-auto max-w-md">
              <div className="flex items-center gap-3 mb-3">
                <AlertCircle className="w-6 h-6 text-red-600" />
                <h3 className="font-bold text-red-800">Error Loading Articles</h3>
              </div>
              <p className="text-red-700 mb-4">{error}</p>
              <button
                onClick={fetchArticles}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-medium"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Current Article */}
          {!isLoading && !error && currentArticle && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              {/* Article Card */}
              <div className="bg-white border-2 sm:border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] sm:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] mb-2 sm:mb-4">
                {/* Article Image */}
                {currentArticle.featured_image_url && (
                  <div className="h-32 sm:h-48 bg-gray-200 overflow-hidden">
                    <img
                      src={currentArticle.featured_image_url}
                      alt={currentArticle.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                <div className="p-8">
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <div className="flex flex-wrap items-center gap-4">
                      <span className="bg-[#0395fc] text-white px-3 py-1 rounded-full text-sm font-bold">
                        Article of the Week
                      </span>
                  
                    </div>
                    <div className="flex items-center">
                      <div className="relative share-menu-container">
                        <Button 
                          variant="outline"
                          size="sm"
                          className="border-2 border-gray-300 bg-gray-100 hover:bg-gray-200"
                          onClick={() => setShowShareMenu(!showShareMenu)}
                        >
                          <Share2 className="h-4 w-4 mr-2" />
                          Share
                        </Button>
                        
                        {/* Share Menu Dropdown */}
                        {showShareMenu && (
                          <div className="absolute top-full mt-2 right-0 w-48 bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] z-50">
                            <button
                              onClick={() => handleShareToSocial('twitter')}
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium"
                            >
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                              </svg>
                              Share on X
                            </button>
                            <button
                              onClick={() => handleShareToSocial('facebook')}
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium"
                            >
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                              </svg>
                              Share on Facebook
                            </button>
                            <button
                              onClick={() => handleShareToSocial('linkedin')}
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium"
                            >
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                              </svg>
                              Share on LinkedIn
                            </button>
                            <button
                              onClick={() => handleShareToSocial('email')}
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium"
                            >
                              <Mail className="h-4 w-4" />
                              Share via Email
                            </button>
                            <button
                              onClick={handleCopyLink}
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium border-t border-gray-200"
                            >
                              <ExternalLink className="h-4 w-4" />
                              {copySuccess ? '✓ Copied!' : 'Copy Link'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <h2 className="text-4xl lg:text-5xl font-black mb-4 leading-tight">
                    {currentArticle.title}
                  </h2>
                  {currentArticle.subtitle && (
                    <p className="text-xl text-gray-600 mb-6">{currentArticle.subtitle}</p>
                  )}

                  <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-6">
                    <div className="flex items-center gap-2">
                      <User className="size-4" />
                      <span className="font-bold">{currentArticle.author}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="size-4" />
                      <span>{currentArticle.publishDate}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="size-4" />
                      <span>{currentArticle.readTime}</span>
                    </div>
                  </div>

                  {/* Article Summary */}
                  <div className="bg-gray-50 p-6 rounded-lg mb-2">
                    <div 
                      className="text-md text-gray-700 leading-relaxed prose prose-lg max-w-none text-left"
                      dangerouslySetInnerHTML={{ __html: currentArticle.summary }}
                    />
                  </div>

                  {/* Tags */}
                  {currentArticle.tags && currentArticle.tags.length > 0 && (
                    <div className="flex flex-wrap gap-3 mb-6">
                      {currentArticle.tags.map((tag, index) => (
                        <motion.span
                          key={index}
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          viewport={{ once: true }}
                          className="bg-[#0395fc] text-white px-4 py-2 font-bold border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] transition-all duration-200"
                        >
                          #{tag}
                        </motion.span>
                      ))}
                    </div>
                  )}

                  {/* Read Full Article Button */}
                  <Link
                    to={`/article/${currentArticle.slug}`}
                    className="inline-flex items-center gap-2 bg-[#0395fc] hover:bg-[#0395fc]/90 text-white px-8 py-4 font-bold border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200"
                  >
                    <BookOpen className="size-5" />
                    Read Full Article
                  </Link>
                </div>
              </div>
            </motion.div>
          )}

          {/* No Current Article State */}
          {!isLoading && !error && !currentArticle && (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-600 mb-2">No Articles Yet</h3>
              <p className="text-gray-500">
                Check back soon for our first article of the week!
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Previous Articles Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-black mb-6">Previous Articles</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Explore our archive of carefully curated articles from previous weeks
            </p>
          </motion.div>

          {!isLoading && !error && (
            <>
              {previousArticles.length > 0 ? (
                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
                  {previousArticles.map((article, index) => (
                    <Link key={article.id} to={`/article/${article.slug}`}>
                      <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        whileHover={{ y: -5 }}
                        className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[12px_12px_0px_0px_rgba(3,149,252,0.3)] transition-all duration-300 group cursor-pointer"
                      >
                        {/* Article Image */}
                        {article.featured_image_url && (
                          <div className="h-10 sm:h-20 bg-gray-200 overflow-hidden">
                            <img
                              src={article.featured_image_url}
                              alt={article.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.style.display = 'none';
                              }}
                            />
                          </div>
                        )}

                        <div className="bg-[#0395fc] text-white p-4 group-hover:bg-[#0070c0] transition-colors">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-bold">Week {article.weekNumber}</span>
                            <span className="bg-white/20 px-2 py-1 rounded text-xs font-bold">
                              Article of the Week
                            </span>
                          </div>
                        </div>

                        <div className="p-3 sm:p-6">
                          <h3 className="text-lg font-black mb-3 line-clamp-2 group-hover:text-[#0395fc] transition-colors">
                            {article.title}
                          </h3>

                          <div className="space-y-2 text-sm text-muted-foreground mb-4">
                            <div className="flex items-center gap-2">
                              <User className="size-4" />
                              <span>{article.author}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="size-4" />
                              <span>{article.readTime}</span>
                            </div>
                          </div>

                          <div className="text-[#0395fc] font-bold hover:underline flex items-center gap-2">
                            Read Article
                            <ExternalLink className="size-4" />
                          </div>
                        </div>
                      </motion.div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-bold text-gray-600 mb-2">No Previous Articles</h4>
                  <p className="text-gray-500 font-[var(--font-dm-sans)]">
                    Previous articles will appear here once we publish more content.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-24 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-black mb-6">
              Never Miss an Article
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
             Join our community of ML enthusiasts and stay updated with the latest articles, research insights, and exclusive content every week.
            </p>
            <motion.a
              href="https://airtable.com/appbzvXorTGCwSsy9/pagrrVOHqpocEY5IX/form"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block bg-[#0395fc] hover:bg-[#0395fc]/90 text-white px-8 py-4 text-lg font-bold border-4 border-[#0395fc] shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)] hover:shadow-[6px_6px_0px_0px_rgba(255,255,255,0.3)] transition-all duration-200"
            >
              Join DMLS
            </motion.a>
          </motion.div>
        </div>
      </section>

      <Footer2 />
    </div>
  );
};

export default ArticleOfTheWeek;